import { formatCategory, formatElement, formatStatLine } from '../../components/GameTerms';
import { renderItemIcon } from '../../components/ItemIcon';
import { getConfig, escapeHtml } from './types';
import type { SwitchChapterFn } from './types';
import type { AffixDef, ConfigData } from '../../../types';

export interface ItemsState {
  categoryFilter: string;
  searchTerm: string;
  /** When set, show the affix detail view for this base ID */
  selectedBase: string | null;
  /** Expanded affix IDs in the detail view */
  expandedAffixIds: Set<string>;
}

export function createItemsState(): ItemsState {
  return { categoryFilter: '', searchTerm: '', selectedBase: null, expandedAffixIds: new Set() };
}

const CATEGORIES: string[] = ['weapon', 'armor', 'offhand', 'accessory'];

/** Title-case a family id: 'kite_shield' → 'Kite Shield'. */
function familyLabel(family: string): string {
  return family.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export function renderItems(content: HTMLElement, state: ItemsState, switchChapter: SwitchChapterFn, _setAffixBaseFilter: (base: string) => void): void {
  if (state.selectedBase) {
    renderItemAffixDetail(content, state);
    return;
  }
  renderItemsList(content, state, switchChapter);
}

// ── Items List View ──────────────────────────────────────────

function renderItemsList(content: HTMLElement, state: ItemsState, _switchChapter: SwitchChapterFn): void {
  const config = getConfig();
  if (!config) { content.innerHTML = '<p>Loading...</p>'; return; }

  const allBases = Object.values(config.itemBases);

  const categoryPills = CATEGORIES.map(cat =>
    `<button class="codex-pill${state.categoryFilter === cat ? ' active' : ''}" data-item-cat="${cat}">${formatCategory(cat)}</button>`
  ).join('');

  // Render the static chrome ONCE per full render. The result list gets
  // populated by renderItemResults below, which is also what the search
  // input handler calls — keeping the input element alive across keystrokes.
  content.innerHTML = `
    <h2 class="codex-section-title">Items</h2>
    <p class="codex-section-subtitle">${allBases.length} bases</p>
    <div class="codex-filter-pills">
      <button class="codex-pill${!state.categoryFilter ? ' active' : ''}" data-item-cat="">All</button>
      ${categoryPills}
    </div>
    <input type="text" class="codex-filter-input" id="codex-item-search" placeholder="Search items..." value="${escapeHtml(state.searchTerm)}" />
    <div id="codex-items-results"></div>
  `;

  // Search input — keystroke only re-renders the result list, never destroys
  // the input element, so focus + caret are preserved naturally.
  content.querySelector('#codex-item-search')?.addEventListener('input', (e) => {
    state.searchTerm = (e.target as HTMLInputElement).value;
    renderItemResults(content, state, _switchChapter);
  });

  // Category pill clicks toggle filters and trigger a full re-render so the
  // active class on pills updates. Focus loss is not a concern — the user
  // just clicked a pill, not typed in the search box.
  content.querySelectorAll('[data-item-cat]').forEach(btn => {
    btn.addEventListener('click', () => {
      state.categoryFilter = (btn as HTMLElement).dataset.itemCat || '';
      renderItems(content, state, _switchChapter, () => {});
    });
  });

  // Initial population of the results.
  renderItemResults(content, state, _switchChapter);
}

/**
 * Re-render only the family-grouped result list. Called on every search
 * keystroke. Leaves the search input element alive, so focus + caret are
 * preserved automatically.
 *
 * Re-binds card click handlers because the cards are recreated.
 */
function renderItemResults(content: HTMLElement, state: ItemsState, _switchChapter: SwitchChapterFn): void {
  const config = getConfig();
  if (!config) return;

  const allBases = Object.values(config.itemBases);

  let filtered = state.categoryFilter
    ? allBases.filter(b => b.category === state.categoryFilter)
    : allBases;

  if (state.searchTerm) {
    const term = state.searchTerm.toLowerCase();
    filtered = filtered.filter(b =>
      b.name.toLowerCase().includes(term) ||
      b.family.toLowerCase().includes(term) ||
      b.category.toLowerCase().includes(term)
    );
  }

  // Group by family, sorted
  const familyGroups = new Map<string, typeof filtered>();
  for (const base of filtered) {
    const fam = base.family || base.category;
    if (!familyGroups.has(fam)) familyGroups.set(fam, []);
    familyGroups.get(fam)!.push(base);
  }

  // Sort bases within each family by ilvl first, so the [0] base is the
  // earliest one and we can use it for the family-level sort below.
  for (const bases of familyGroups.values()) {
    bases.sort((a, b) => (a.ilvl || 0) - (b.ilvl || 0));
  }

  // Sort families by category (CATEGORIES order), then by lowest-ilvl base
  // within the family. Fully data-driven — adding a new family or category
  // requires no edits here.
  const sortedFamilies = [...familyGroups.entries()].sort((a, b) => {
    const ac = CATEGORIES.indexOf(a[1][0].category);
    const bc = CATEGORIES.indexOf(b[1][0].category);
    const acIdx = ac === -1 ? 99 : ac;
    const bcIdx = bc === -1 ? 99 : bc;
    if (acIdx !== bcIdx) return acIdx - bcIdx;
    const ai = a[1][0].ilvl ?? 0;
    const bi = b[1][0].ilvl ?? 0;
    if (ai !== bi) return ai - bi;
    return a[0].localeCompare(b[0]);
  });

  const familyHtml = sortedFamilies.map(([family, bases]) => {
    const label = familyLabel(family);
    const familyParked = bases.every(b => b.parked);
    const cards = bases.map(base => {
      const reqs = (base.masteryRequirements ?? []).map(r => {
        const name = config.masteries?.[r.masteryId]?.name ?? r.masteryId;
        return `${name} ${r.rank}`;
      }).join(', ');

      const implicitDef = base.implicitAffixId ? config.affixes?.[base.implicitAffixId] : null;
      const implicitText = implicitDef ? formatImplicitText(implicitDef, base.ilvl || 1) : '';
      const parkedClass = base.parked ? ' codex-item-card--parked' : '';

      return `
        <div class="codex-item-card${parkedClass}" data-item-base="${base.id}">
          <div class="codex-item-icon">${renderItemIcon(base.id, base.category, 'lg')}</div>
          <div class="codex-item-info">
            <div class="codex-item-name">${base.name}</div>
            <div class="codex-item-meta">Lv${base.charLevelRequired || 1}${reqs ? ` · ${reqs}` : ''}</div>
            ${implicitText ? `<div class="codex-item-implicit">${implicitText}</div>` : ''}
          </div>
        </div>
      `;
    }).join('');

    const familyParkedClass = familyParked ? ' codex-family-group--parked' : '';
    const parkedBadge = familyParked ? '<span class="codex-family-parked-badge">Not Yet Droppable</span>' : '';
    return `
      <div class="codex-family-group${familyParkedClass}">
        <div class="codex-family-header">${label}${parkedBadge}</div>
        <div class="codex-skill-grid">${cards}</div>
      </div>
    `;
  }).join('');

  const results = content.querySelector('#codex-items-results');
  if (results) {
    results.innerHTML = familyHtml || '<p class="codex-empty">No items match filters</p>';
  }

  // Re-bind card click handlers — cards are recreated by the innerHTML above.
  content.querySelectorAll('[data-item-base]').forEach(card => {
    card.addEventListener('click', () => {
      state.selectedBase = (card as HTMLElement).dataset.itemBase!;
      renderItems(content, state, _switchChapter, () => {});
      content.scrollTop = 0;
    });
  });
}

// ── Affix Detail View (inline, replaces list) ───────────────

function renderItemAffixDetail(content: HTMLElement, state: ItemsState): void {
  const config = getConfig();
  if (!config || !state.selectedBase) return;

  const base = config.itemBases[state.selectedBase];
  if (!base) { state.selectedBase = null; renderItems(content, state, () => {}, () => {}); return; }

  const tags = new Set(base.tags);
  const ilvl = base.ilvl || 1;
  const allAffixes = Object.values(config.affixes);

  // Filter affixes: must match at least one tag AND have at least one tier at this ilvl
  const eligible = allAffixes.filter(a =>
    a.type !== 'implicit' &&
    a.tags.some(t => tags.has(t)) &&
    a.tiers.some(t => t.requiredLevel <= ilvl)
  );

  // Implicit
  const implicitAffix = config.affixes[base.implicitAffixId];
  const eligibleImplicitTiers = implicitAffix
    ? implicitAffix.tiers.filter(t => t.requiredLevel <= ilvl)
    : [];
  const bestImplicitTier = eligibleImplicitTiers.length > 0
    ? eligibleImplicitTiers[eligibleImplicitTiers.length - 1]
    : null;

  // Split and sort by weight
  const prefixes = eligible.filter(a => a.type === 'prefix').sort((a, b) => poolWeight(b, ilvl) - poolWeight(a, ilvl));
  const suffixes = eligible.filter(a => a.type === 'suffix').sort((a, b) => poolWeight(b, ilvl) - poolWeight(a, ilvl));

  const reqs = (base.masteryRequirements ?? []).map(r => {
    const name = config.masteries?.[r.masteryId]?.name ?? r.masteryId;
    return `${name} ${r.rank}`;
  }).join(', ');

  // Best-implicit line for the hero strip
  let heroImplicitLine = '';
  if (implicitAffix && bestImplicitTier) {
    if (implicitAffix.stat.startsWith('flat_') && implicitAffix.stat.endsWith('_damage') && implicitAffix.element) {
      const elName = implicitAffix.element.charAt(0).toUpperCase() + implicitAffix.element.slice(1);
      const minV = bestImplicitTier.minValue;
      const maxV = bestImplicitTier.maxHigh ?? bestImplicitTier.maxValue;
      heroImplicitLine = `Adds ${minV}–${maxV} ${elName} Damage`;
    } else {
      heroImplicitLine = formatRangedStatLine(implicitAffix, bestImplicitTier.minValue, bestImplicitTier.maxValue);
    }
  }

  content.innerHTML = `
    <button class="codex-back-btn" id="codex-item-back">← Back to Items</button>
    <div class="codex-item-hero">
      <div class="codex-item-hero-icon">${renderItemIcon(base.id, base.category, 'lg')}</div>
      <div class="codex-item-hero-info">
        <h2 class="codex-item-hero-name">${base.name}</h2>
        <div class="codex-item-hero-meta">
          <span class="codex-item-hero-cat">${formatCategory(base.category)}</span>
          <span class="codex-item-hero-sep">·</span>
          <span class="codex-item-hero-lvl">Level ${base.charLevelRequired || 1}</span>
          ${reqs ? `<span class="codex-item-hero-sep">·</span><span class="codex-item-hero-reqs">${reqs}</span>` : ''}
        </div>
        ${implicitAffix ? `
          <div class="codex-item-hero-implicit">
            <span class="codex-item-hero-implicit-label">Innate</span>
            <span class="codex-item-hero-implicit-line">${heroImplicitLine || implicitAffix.name}</span>
          </div>
        ` : ''}
      </div>
    </div>

    <div class="codex-affix-columns">
      ${renderAffixColumn('PREFIXES', prefixes, ilvl, config, state)}
      ${renderAffixColumn('SUFFIXES', suffixes, ilvl, config, state)}
    </div>
  `;

  content.querySelector('#codex-item-back')?.addEventListener('click', () => {
    state.selectedBase = null;
    state.expandedAffixIds.clear();
    renderItems(content, state, () => {}, () => {});
    content.scrollTop = 0;
  });

  // Bind expand/collapse on affix rows
  content.querySelectorAll('[data-affix-expand]').forEach(row => {
    row.addEventListener('click', () => {
      const id = (row as HTMLElement).dataset.affixExpand!;
      if (state.expandedAffixIds.has(id)) state.expandedAffixIds.delete(id);
      else state.expandedAffixIds.add(id);
      renderItemAffixDetail(content, state);
    });
  });
}

function poolWeight(a: AffixDef, ilvl: number): number {
  return a.tiers.filter(t => t.requiredLevel <= ilvl).reduce((s, t) => s + (t.weight ?? a.weight), 0);
}

function renderAffixColumn(label: string, affixes: AffixDef[], ilvl: number, _config: ConfigData, state: ItemsState): string {
  if (affixes.length === 0) return `
    <div class="codex-affix-column">
      <div class="codex-affix-column-header">${label}</div>
      <div class="codex-affix-empty">None</div>
    </div>`;

  const totalWeight = affixes.reduce((s, a) => s + poolWeight(a, ilvl), 0);

  const rows = affixes.map(a => {
    const eligibleTiers = a.tiers.filter(t => t.requiredLevel <= ilvl);
    const bestTier = eligibleTiers[eligibleTiers.length - 1];
    const wt = poolWeight(a, ilvl);
    const pct = totalWeight > 0 ? (wt / totalWeight * 100).toFixed(1) : '0';
    const isExpanded = state.expandedAffixIds.has(a.id);
    const pills = getAffixPills(a);
    // Descale tier values for affixes that store sub-integer precision rolls.
    const scale = a.decimals ? Math.pow(10, a.decimals) : 1;
    const sc = (v: number) => v / scale;

    let html = `
      <div class="codex-affix-row-compact${isExpanded ? ' expanded' : ''}" data-affix-expand="${a.id}">
        <div class="codex-affix-desc-cell">
          <span class="codex-affix-desc-stat">${formatStatDesc(a)}</span>
          <span class="codex-affix-desc-name">${a.name}</span>
          <div class="codex-affix-tag-pills">${pills}</div>
        </div>
        <div class="codex-affix-range">${bestTier
          ? (bestTier.minHigh != null && bestTier.maxHigh != null
            ? `(${sc(bestTier.minValue)}–${sc(bestTier.minHigh)}) to (${sc(bestTier.maxValue)}–${sc(bestTier.maxHigh)})`
            : `${sc(bestTier.minValue)}–${sc(bestTier.maxValue)}`)
          : '—'}</div>
        <div class="codex-affix-weight">${pct}%</div>
      </div>`;

    if (isExpanded) {
      const totalTiersInAffix = a.tiers.length;
      const tierRows = eligibleTiers.map((t, _i) => {
        const globalIndex = a.tiers.indexOf(t);
        const tierNum = totalTiersInAffix - globalIndex; // T1 = best (last)
        const tierWt = t.weight ?? a.weight;
        const tierPct = totalWeight > 0 ? (tierWt / totalWeight * 100).toFixed(2) : '0';
        // Dual-range for flat damage, single range for everything else
        const isDualRange = t.minHigh != null && t.maxHigh != null;
        const rangeText = isDualRange
          ? `(${sc(t.minValue)}–${sc(t.minHigh!)}) to (${sc(t.maxValue)}–${sc(t.maxHigh!)})`
          : `${sc(t.minValue)}–${sc(t.maxValue)}`;
        return `<div class="codex-affix-tier-detail">
          <span class="codex-tier-label">T${tierNum}</span>
          <span class="codex-tier-range">${rangeText}</span>
          <span class="codex-tier-lvl">Lv${t.requiredLevel}+</span>
          <span class="codex-tier-wt">${tierWt}</span>
          <span class="codex-tier-pct">${tierPct}%</span>
        </div>`;
      }).join('');
      html += `<div class="codex-affix-tier-breakdown">${tierRows}</div>`;
    }

    return html;
  }).join('');

  return `
    <div class="codex-affix-column">
      <div class="codex-affix-column-header">${label} <span class="codex-affix-count">(${affixes.length})</span></div>
      ${rows}
    </div>`;
}

function formatStatDesc(a: AffixDef): string {
  // For flat damage stats, the template includes the range math — use a clean format
  if (a.stat.startsWith('flat_') && a.stat.endsWith('_damage') && a.element) {
    const elName = a.element.charAt(0).toUpperCase() + a.element.slice(1);
    return `Adds #-# ${elName} Damage`;
  }
  // Use the actual game stat template with a sentinel value, then replace with #
  const SENTINEL = 12345;
  const line = formatStatLine(a.stat, a.modifier, SENTINEL);
  return line.replace(/12345/g, '#');
}

/** Format an implicit affix as a short inline stat line showing the best-tier roll at a base's ilvl. */
function formatImplicitText(a: AffixDef, ilvl: number): string {
  const eligible = a.tiers.filter(t => t.requiredLevel <= ilvl);
  const best = eligible[eligible.length - 1];
  if (!best) return a.name;

  // Flat damage affixes have a dual-range — show "Adds X-Y Element Damage"
  if (a.stat.startsWith('flat_') && a.stat.endsWith('_damage') && a.element) {
    const elName = a.element.charAt(0).toUpperCase() + a.element.slice(1);
    const minVal = best.minValue;
    const maxVal = best.maxHigh ?? best.maxValue;
    return `Adds ${minVal}-${maxVal} ${elName} Damage`;
  }

  return formatRangedStatLine(a, best.minValue, best.maxValue);
}

/** Format a stat line that may have a roll range. Shows "+1–3 to Speed" when min ≠ max,
 *  collapses to "+2 to Speed" when min === max. Substitutes the max number in the
 *  template-rendered line with "min–max" when there is variance. Descales when the affix
 *  uses sub-integer precision storage (decimals). */
function formatRangedStatLine(a: AffixDef, minValue: number, maxValue: number): string {
  const scale = a.decimals ? Math.pow(10, a.decimals) : 1;
  const minScaled = minValue / scale;
  const maxScaled = maxValue / scale;
  if (minScaled === maxScaled) {
    return formatStatLine(a.stat, a.modifier, minScaled);
  }
  const maxLine = formatStatLine(a.stat, a.modifier, maxScaled);
  // Replace the first occurrence of the max number (with optional leading +) with "min–max"
  return maxLine.replace(new RegExp(`\\+?${maxScaled}\\b`), `${minScaled}–${maxScaled}`);
}

/** Generate descriptive tag pills for an affix based on what it does */
function getAffixPills(a: AffixDef): string {
  const pills: string[] = [];
  const pill = (label: string, cls: string) => `<span class="codex-affix-pill ${cls}">${label}</span>`;

  // Element pill for flat damage
  if (a.element) {
    pills.push(pill(formatElement(a.element), `el-${a.element}`));
  }

  // Condition tag for tag_increased
  if (a.conditionTag) {
    const label = a.conditionTag.charAt(0).toUpperCase() + a.conditionTag.slice(1);
    pills.push(pill(label, 'cat-tag_increased'));
  }

  // Stat-based pills
  const s = a.stat;
  if (s === 'crit_avoidance') {
    pills.push(pill('Crit Avoid', 'pill-crit'));
  } else if (s.includes('crit')) {
    pills.push(pill('Crit', 'pill-crit'));
  }
  if (s === 'spd' || s === 'global_momentum_reduction' || s === 'cornered_spd' || s === 'speed_on_kill') {
    pills.push(pill('Speed', 'pill-speed'));
  }
  if (s === 'hp' || s === 'life_on_hit' || s === 'hp_regen' || s === 'hp_on_struck') {
    pills.push(pill('Health', 'pill-hp'));
  }
  if (s === 'life_on_hit' || s === 'hp_regen' || s === 'hp_on_struck' || s === 'lifesteal' || s === 'armor_lifesteal' || s === 'healing_done' || s === 'healing_received' || s === 'heal_on_kill' || s === 'heal_on_combat_end') {
    pills.push(pill('Sustain', 'pill-sustain'));
  }
  if (s === 'healing_done' || s === 'healing_received') {
    pills.push(pill('Healing', 'pill-healing'));
  }
  if (s === 'focus' || s === 'focus_percent' || s === 'focus_regen' || s === 'focus_on_crit' || s === 'focus_on_kill' || s === 'focus_on_hit' || s === 'focus_on_hp_regen' || s === 'focus_on_struck_armor' || s === 'global_focus_cost_reduction') {
    pills.push(pill('Focus', 'pill-focus'));
  }
  if (s.includes('_pen')) {
    pills.push(pill('Penetration', 'pill-pen'));
  }
  if (s.includes('dot') || s.includes('burn') || s.includes('bleed') || s.includes('poison') || s.includes('decay')) {
    pills.push(pill('DoT', 'pill-dot'));
  }
  if (s.includes('burn_dot')) pills.push(pill('Burn', 'pill-burn'));
  if (s.includes('bleed_dot')) pills.push(pill('Bleed', 'pill-bleed'));
  if (s.includes('poison_dot')) pills.push(pill('Poison', 'pill-poison'));
  if (s.includes('decay_dot')) pills.push(pill('Decay', 'pill-decay'));
  if (s === 'status_resistance' || s === 'dot_damage_taken') {
    pills.push(pill('Status', 'pill-status'));
  }
  if (s === 'barrier_potency') pills.push(pill('Barrier', 'pill-shield'));
  if (s === 'impact_damage') pills.push(pill('Impact', 'pill-impact'));
  if (s === 'fervor_potency') pills.push(pill('Fervor', 'pill-fervor'));
  if (s === 'trap_potency') pills.push(pill('Trap', 'pill-trap'));
  if (s === 'charged_potency') pills.push(pill('Charged', 'pill-charged'));
  if (s === 'tempo_generation') pills.push(pill('Tempo', 'pill-tempo'));
  if (s === 'hex_potency') pills.push(pill('Hex', 'pill-debuff'));
  if (s === 'ruin_potency') pills.push(pill('Ruin', 'pill-debuff'));
  if (s === 'debuff_duration' || s === 'debuff_duration_flat' || s === 'chance_inflict_exposed') pills.push(pill('Debuff', 'pill-debuff'));
  if (s === 'boon_potency') pills.push(pill('Boon', 'pill-buff'));
  if (s === 'buff_duration' || s === 'buff_duration_flat') pills.push(pill('Buff', 'pill-buff'));
  if (s === 'extra_combo_hits') pills.push(pill('Combo', 'pill-combo'));
  if (s === 'chain_chance') pills.push(pill('Chain', 'pill-chain'));
  if (s === 'increased_damage') pills.push(pill('Damage', 'pill-damage'));

  // Resistance group + single-element resists
  if (s === 'elemental_res') pills.push(pill('Elemental', 'pill-elemental'));
  if (s === 'primal_res') pills.push(pill('Primal', 'pill-primal'));
  if (s === 'cosmic_res') pills.push(pill('Cosmic', 'pill-cosmic'));
  if (s === 'steel_res') pills.push(pill('Steel', 'el-steel'));
  if (s === 'brute_res') pills.push(pill('Brute', 'el-brute'));
  if (s === 'finesse_res') pills.push(pill('Finesse', 'el-finesse'));
  // Single-element resists (v16 armor family signatures)
  if (s === 'fire_res') pills.push(pill('Fire', 'el-fire'));
  if (s === 'water_res') pills.push(pill('Water', 'el-water'));
  if (s === 'lightning_res') pills.push(pill('Lightning', 'el-lightning'));
  if (s === 'nature_res') pills.push(pill('Nature', 'el-nature'));
  if (s === 'wind_res') pills.push(pill('Wind', 'el-wind'));
  if (s === 'earth_res') pills.push(pill('Earth', 'el-earth'));
  if (s === 'holy_res') pills.push(pill('Holy', 'el-holy'));
  if (s === 'dark_res') pills.push(pill('Dark', 'el-dark'));
  if (s === 'astral_res') pills.push(pill('Astral', 'el-astral'));
  if (s.includes('_res')) pills.push(pill('Resistance', 'pill-res'));
  if (s === 'physical_pen') pills.push(pill('Physical', 'pill-physical'));
  if (s === 'elemental_pen') pills.push(pill('Elemental', 'pill-elemental'));
  if (s === 'primal_pen') pills.push(pill('Primal', 'pill-primal'));
  if (s === 'cosmic_pen') pills.push(pill('Cosmic', 'pill-cosmic'));

  // ── v16 armor family signature pills ──
  if (s === 'guard') pills.push(pill('Guard', 'pill-guard'));
  if (s === 'cornered_spd' || s === 'momentum_on_struck' || s === 'hp_on_struck' || s === 'focus_on_struck_armor' || s === 'thorns_damage') {
    pills.push(pill('On Struck', 'pill-on-struck'));
  }
  if (s === 'first_strike_bonus') pills.push(pill('First Strike', 'pill-first-strike'));
  if (s === 'armor_lifesteal') pills.push(pill('Leech', 'pill-leech'));
  if (s === 'crit_avoidance') pills.push(pill('Mitigation', 'pill-mitigation'));
  if (s === 'speed_on_kill' || s === 'heal_on_kill') pills.push(pill('On Kill', 'pill-on-kill'));
  if (s === 'heal_on_combat_end') pills.push(pill('Recovery', 'pill-recovery'));

  return pills.join('');
}
